import React, { useState } from 'react';
import { MessageCircle, Send, Loader, Sparkles, RotateCcw } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

export default function AIPreviewSection() {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const sampleQuestions = [
    "Bonjour, je veux envoyer des colis en Côte d'Ivoire",
    "Quels sont vos tarifs pour un conteneur complet?",
    "Est-ce que vous transportez les voitures?",
    "Je peux envoyer un réfrigérateur?",
    "Quel est le délai de livraison?",
    "Avez-vous des promotions en ce moment?",
    "Quels documents je dois préparer?",
    "Comment je peux suivre mon colis?",
    "C'est quoi la différence entre groupage et conteneur?",
    "Vous livrez à domicile à Abidjan?"
  ];

  const sendMessage = async (messageText) => {
    const text = messageText || inputMessage;
    if (!text?.trim()) return;

    // Add user message
    const userMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const { data, error } = await supabase?.functions?.invoke('fatou-chat', {
        body: {
          messages: [...messages, userMessage]
        }
      });

      if (error) throw error;

      // Add assistant message
      const assistantMessage = { 
        role: 'assistant', 
        content: data?.message || 'Désolé, je n\'ai pas pu générer de réponse.' 
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = { 
        role: 'assistant', 
        content: `Erreur: ${error?.message || 'Impossible de contacter le chatbot'}` 
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const resetConversation = () => {
    setMessages([]);
    setInputMessage('');
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Sparkles className="text-green-600" size={24} />
          <h2 className="text-xl font-semibold text-gray-900">Aperçu IA en Temps Réel</h2>
        </div>
        {messages?.length > 0 && (
          <button
            onClick={resetConversation}
            className="flex items-center space-x-2 px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            <RotateCcw size={16} />
            <span>Réinitialiser</span>
          </button>
        )}
      </div>

      <p className="text-gray-600 text-sm mb-6">
        Testez Fatou en temps réel avec la configuration actuelle. Les réponses utilisent les données enregistrées dans la base de données.
      </p>

      {/* Sample Questions */}
      {messages?.length === 0 && (
        <div className="bg-gradient-to-br from-green-50 to-blue-50 border border-green-200 rounded-lg p-4 mb-4">
          <h3 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
            <MessageCircle size={18} className="text-green-600" />
            <span>Questions Suggérées</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {sampleQuestions?.map((question, index) => (
              <button
                key={index}
                onClick={() => sendMessage(question)}
                className="text-left px-3 py-2 bg-white border border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors text-sm text-gray-700"
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Chat Interface */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {/* Messages Area */}
        <div className="h-96 overflow-y-auto p-4 space-y-4">
          {messages?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <MessageCircle size={48} className="text-gray-300 mb-3" />
              <p className="text-gray-500">Commencez une conversation avec Fatou</p>
              <p className="text-sm text-gray-400 mt-1">Cliquez sur une question suggérée ou tapez votre message</p>
            </div>
          ) : (
            messages?.map((message, index) => (
              <div
                key={index}
                className={`flex ${message?.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg px-4 py-2 ${
                    message?.role === 'user' ?'bg-green-600 text-white' :'bg-gray-100 text-gray-900'
                  }`}
                >
                  {message?.role === 'assistant' && (
                    <div className="flex items-center space-x-2 mb-1">
                      <img 
                        src="/assets/images/fatou_avatar_african_woman.png" 
                        alt="Fatou"
                        className="w-6 h-6 rounded-full"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <span className="text-xs font-semibold text-gray-600">Fatou</span>
                    </div>
                  )}
                  <p className="text-sm whitespace-pre-wrap">{message?.content}</p>
                </div>
              </div>
            ))
          )}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-lg px-4 py-2">
                <div className="flex items-center space-x-2">
                  <Loader className="animate-spin text-gray-600" size={16} />
                  <span className="text-sm text-gray-600">Fatou réfléchit...</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="border-t border-gray-200 p-4">
          <div className="flex space-x-2">
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e?.target?.value)}
              onKeyPress={handleKeyPress}
              placeholder="Tapez votre message..."
              rows={2}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
              disabled={loading}
            />
            <button
              onClick={() => sendMessage()}
              disabled={loading || !inputMessage?.trim()}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <Send size={18} />
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Appuyez sur Entrée pour envoyer, Shift+Entrée pour une nouvelle ligne
          </p>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>💡 Astuce:</strong> Cet aperçu utilise la configuration actuelle enregistrée. Pour tester vos dernières modifications, assurez-vous d'avoir cliqué sur "Enregistrer la Configuration" avant de tester.
        </p>
      </div>
    </div>
  );
}